import { useEffect } from 'react';
import { NextParsedUrlQuery } from 'next/dist/server/request-meta';

import { IAnalyticsMetadata } from '../../types/contentful';

import analytics from '../utils/analytics';
import { getCookie } from '../utils/cookies';
import { getDeviceType } from '../utils/detectDevice';
import { setRumUser, trackRumEvent } from '../utils/datadogRum';
import { getAnonymousId } from '../utils/getAnonymousId';
import { Educator } from '../context/User';
import { getExperimentProperties } from '../utils/mixpanel';
import { getPartnerTrackingProperties } from '../utils/getPartnerTrackingProperties';

interface PageAnalytics {
  analyticsMetadata?: IAnalyticsMetadata;
  query?: NextParsedUrlQuery;
  slug?: string;
  pathPrefix?: string;
  user?: Educator;
}

export function usePageAnalytics({
  analyticsMetadata,
  query,
  slug,
  pathPrefix,
  user
}: PageAnalytics) {
  useEffect(() => {
    const userId = getAnonymousId();

    const referrer = window?.document?.referrer;

    const pageEvent: Record<string, unknown> = {
      ...query,
      anonymousId: userId,
      deviceType: getDeviceType(),
      returningUser: getCookie('gl_is_returning_user'),
      registeredUser: getCookie('gl_is_registered_user'),
      utm_source: query?.utm_source || getCookie('utm_source') || 'not_set',
      direct_utm: query?.utm_source || referrer || 'not_set'
    };

    // Set path for pageEvent from slug and pathPrefix
    if (slug) {
      const url = slug === 'homepage' ? '/' : `/${slug}`;
      pageEvent.path = pathPrefix ? `${pathPrefix}${url}` : url;
    }

    if (user) {
      pageEvent.email = user.email;
    }

    // Add experiment properties from session storage if available
    // This ensures page events have experiment context even if experiment tracking hasn't fired yet
    const experimentProperties = getExperimentProperties();
    Object.assign(pageEvent, experimentProperties);

    // Add partner tracking properties
    const partnerProperties = getPartnerTrackingProperties();
    Object.assign(pageEvent, partnerProperties);

    // Update RUM context with userId
    setRumUser({ id: userId });

    // Ensure Segment anonId is in sync with userId
    analytics.setAnonymousId(userId);

    // Identify user if authenticated to ensure proper user tracking
    if (user?.email) {
      analytics.identify(user.email, {
        email: user.email,
        name: `${user.givenName ?? ''} ${user.familyName ?? ''}`.trim(),
        state: user.state,
        gradeLevel: user.gradeLevel,
        role: user.role,
        school: user.school,
        ...partnerProperties
      });
    }

    // Trigger Segment Loaded a Page event
    analytics.page(
      analyticsMetadata?.fields?.category || '',
      analyticsMetadata?.fields?.name || '',
      pageEvent
    );

    trackRumEvent('Loaded a Page', {
      referrer,
      ...analyticsMetadata?.fields,
      ...pageEvent
    });

    // Mixpanel is loaded by segment in device mode, meaning their anonymous ids diverge when using anonymous id on backend.
    // Doing an alias here should fix that to stitch mixpanel's anonid and segment's
    (function aliasMixpanel() {
      if (window.mixpanel && typeof window.mixpanel.alias === 'function') {
        window.mixpanel.alias(analytics.anonymousId as string);
      } else {
        setTimeout(aliasMixpanel, 2000); // if mixpanel doesnt exist yet, try again in 2 seconds
      }
    })();
  }, [analyticsMetadata]);
}
